import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Autocomplete,
} from '@mui/material'
import {
  Save,
  ArrowBack,
  Add,
  Delete,
  Edit,
  EmojiEvents,
  MilitaryTech,
  Settings,
  Search,
  Person,
  Group,
} from '@mui/icons-material'
import {
  useCollection,
  useCreateCollection,
  useUpdateCollection,
  useAvailableAdventures,
  useSetCollectionBadge,
  useDeleteCollectionBadge,
  useCreateCollectionAchievement,
  useUpdateCollectionAchievement,
  useDeleteCollectionAchievement,
  useAddAchievementAdventure,
  useRemoveAchievementAdventure,
  useCollectionAchievement,
  useCollectionMaintainers,
  useAddCollectionMaintainer,
  useRemoveCollectionMaintainer,
} from '../hooks/useAdventureCollections'
import type {
  AvailableAdventureDto,
  CollectionBadgeDto,
  CreateCollectionBadgeDto,
  CollectionAchievementSummaryDto,
  CollectionAchievementDto,
  CollectionMaintainerDto,
} from '@api/types/adventureCollection'
import { adventureCollectionsApi } from '@api/endpoints/adventureCollections'
import { searchApi } from '@api/endpoints/search'
import { SearchEntityType } from '@api/types/search'
import type { UserSearchResult, GroupSearchResult } from '@api/types/search'
import { ImageUploadZone } from '@components/common/ImageUploadZone'


// Unified search result type for users and groups in maintainer search
interface MaintainerSearchResult {
  id: string
  type: 'user' | 'group'
  displayName: string
  profileImageUrl?: string
  userId?: string
  groupId?: string
  description?: string
  memberCount?: number
}

export default function CollectionEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isEditMode = !!id

  // Form state
  const [name, setName] = useState('')
  const [shortDescription, setShortDescription] = useState('')
  const [fullDescription, setFullDescription] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Badge dialog state
  const [badgeDialogOpen, setBadgeDialogOpen] = useState(false)
  const [badgeName, setBadgeName] = useState('')
  const [badgeDescription, setBadgeDescription] = useState('')
  const [badgeLongDescription, setBadgeLongDescription] = useState('')
  const [badgeError, setBadgeError] = useState<string | null>(null)
  const [badgeImageUrl, setBadgeImageUrl] = useState<string | null>(null)
  const [badgeImageId, setBadgeImageId] = useState<string | null>(null)
  const [uploadedBadgeFile, setUploadedBadgeFile] = useState<File | null>(null)
  const [isUploadingBadgeImage, setIsUploadingBadgeImage] = useState(false)

  // Delete badge confirmation
  const [deleteBadgeDialogOpen, setDeleteBadgeDialogOpen] = useState(false)

  // Achievement dialog state
  const [achievementDialogOpen, setAchievementDialogOpen] = useState(false)
  const [editingAchievement, setEditingAchievement] = useState<CollectionAchievementDto | null>(null)
  const [achievementName, setAchievementName] = useState('')
  const [achievementShortDesc, setAchievementShortDesc] = useState('')
  const [achievementFullDesc, setAchievementFullDesc] = useState('')
  const [trackFailures, setTrackFailures] = useState(false)
  const [achievementError, setAchievementError] = useState<string | null>(null)
  const [achievementIconUrl, setAchievementIconUrl] = useState<string | null>(null)
  const [achievementIconId, setAchievementIconId] = useState<string | null>(null)
  const [uploadedIconFile, setUploadedIconFile] = useState<File | null>(null)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)

  // Achievement adventures dialog
  const [achievementAdventuresDialogOpen, setAchievementAdventuresDialogOpen] = useState(false)
  const [selectedAchievementId, setSelectedAchievementId] = useState<string | null>(null)
  const [adventureSearch, setAdventureSearch] = useState('')
  const [adventureSortField, setAdventureSortField] = useState<'title' | 'createdDate'>('createdDate')
  const [adventureSortDirection, setAdventureSortDirection] = useState<'asc' | 'desc'>('desc')

  // Delete achievement confirmation
  const [deleteAchievementDialogOpen, setDeleteAchievementDialogOpen] = useState(false)
  const [achievementToDelete, setAchievementToDelete] = useState<CollectionAchievementSummaryDto | null>(null)

  // Maintainer management state
  const [maintainerSearchQuery, setMaintainerSearchQuery] = useState('')
  const [maintainerSearchResults, setMaintainerSearchResults] = useState<MaintainerSearchResult[]>([])
  const [isMaintainerSearching, setIsMaintainerSearching] = useState(false)
  const [maintainerError, setMaintainerError] = useState<string | null>(null)
  const [removeMaintainerDialogOpen, setRemoveMaintainerDialogOpen] = useState(false)
  const [maintainerToRemove, setMaintainerToRemove] = useState<CollectionMaintainerDto | null>(null)

  // Queries
  const { data: collection, isLoading: isLoadingCollection } = useCollection(id)
  const { data: availableAdventures, isLoading: isLoadingAvailable } = useAvailableAdventures(id)
  const { data: selectedAchievement, isLoading: isLoadingSelectedAchievement } = useCollectionAchievement(id, selectedAchievementId ?? undefined)
  const { data: maintainers, isLoading: isLoadingMaintainers } = useCollectionMaintainers(id)

  // Mutations
  const createCollection = useCreateCollection()
  const updateCollection = useUpdateCollection()
  const setBadge = useSetCollectionBadge()
  const deleteBadge = useDeleteCollectionBadge()
  const createAchievement = useCreateCollectionAchievement()
  const updateAchievement = useUpdateCollectionAchievement()
  const deleteAchievement = useDeleteCollectionAchievement()
  const addAchievementAdventure = useAddAchievementAdventure()
  const removeAchievementAdventure = useRemoveAchievementAdventure()
  const addMaintainer = useAddCollectionMaintainer()
  const removeMaintainer = useRemoveCollectionMaintainer()

  // Populate form when editing
  useEffect(() => {
    if (collection) {
      setName(collection.name)
      setShortDescription(collection.shortDescription || '')
      setFullDescription(collection.fullDescription || '')
      setIsPublic(collection.isPublic)
    }
  }, [collection])

  // Search users and groups for maintainer autocomplete (debounced)
  useEffect(() => {
    const searchMaintainers = async () => {
      if (!maintainerSearchQuery || maintainerSearchQuery.length < 2) {
        setMaintainerSearchResults([])
        return
      }

      try {
        setIsMaintainerSearching(true)
        const response = await searchApi.search({
          query: maintainerSearchQuery,
          type: SearchEntityType.User | SearchEntityType.Group, // type=6: Users and Groups
          take: 10,
        })

        // Map users to MaintainerSearchResult format
        const userResults: MaintainerSearchResult[] = (response.users || []).map((user: UserSearchResult) => ({
          id: user.userId,
          type: 'user' as const,
          displayName: user.displayName,
          profileImageUrl: user.profilePicture || user.avatar,
          userId: user.userId,
        }))

        // Map groups to MaintainerSearchResult format
        const groupResults: MaintainerSearchResult[] = (response.groups || []).map((group: GroupSearchResult) => ({
          id: group.id,
          type: 'group' as const,
          displayName: group.name,
          profileImageUrl: group.groupImage,
          groupId: group.id,
          memberCount: group.memberCount,
          description: group.description,
        }))

        // Combine users first, then groups
        setMaintainerSearchResults([...userResults, ...groupResults])
      } catch (err) {
        console.error('Error searching for maintainers:', err)
        setMaintainerSearchResults([])
      } finally {
        setIsMaintainerSearching(false)
      }
    }

    const timer = setTimeout(searchMaintainers, 300)
    return () => clearTimeout(timer)
  }, [maintainerSearchQuery])

  const handleSave = async () => {
    setFormError(null)

    if (!name.trim()) {
      setFormError('Collection name is required')
      return
    }

    try {
      if (isEditMode && id) {
        await updateCollection.mutateAsync({
          id,
          data: {
            name: name.trim(),
            shortDescription: shortDescription.trim() || null,
            fullDescription: fullDescription.trim() || null,
            isPublic,
          },
        })
        navigate('/my-adventures')
      } else {
        const newCollection = await createCollection.mutateAsync({
          name: name.trim(),
          shortDescription: shortDescription.trim() || null,
          fullDescription: fullDescription.trim() || null,
          isPublic,
        })
        navigate(`/collections/${newCollection.id}`)
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to save collection'
      setFormError(message)
    }
  }

  // Badge handlers
  const openBadgeDialog = () => {
    // Pre-populate with existing badge if present
    if (collection?.badge) {
      setBadgeName(collection.badge.name)
      setBadgeDescription(collection.badge.description || '')
      setBadgeLongDescription(collection.badge.longDescriptionHtml || '')
      setBadgeImageUrl(collection.badge.imageUrl || null)
      setBadgeImageId(collection.badge.imageId || null)
    } else {
      setBadgeName('')
      setBadgeDescription('')
      setBadgeLongDescription('')
      setBadgeImageUrl(null)
      setBadgeImageId(null)
    }
    setUploadedBadgeFile(null)
    setBadgeError(null)
    setBadgeDialogOpen(true)
  }

  const handleBadgeImageSelect = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setBadgeImageUrl(reader.result as string)
      setUploadedBadgeFile(file)
    }
    reader.readAsDataURL(file)
  }

  const handleBadgeImageClear = () => {
    setBadgeImageUrl(null)
    setBadgeImageId(null)
    setUploadedBadgeFile(null)
  }

  const handleSaveBadge = async () => {
    if (!id) return
    setBadgeError(null)

    if (!badgeName.trim()) {
      setBadgeError('Badge name is required')
      return
    }

    try {
      // Upload badge image if a new file was selected
      let finalImageId = badgeImageId
      if (uploadedBadgeFile) {
        setIsUploadingBadgeImage(true)
        try {
          const uploadResult = await adventureCollectionsApi.uploadBadgeImage(uploadedBadgeFile)
          finalImageId = uploadResult.id
        } catch (uploadError: any) {
          setBadgeError('Failed to upload image: ' + (uploadError.response?.data?.message || uploadError.message || 'Unknown error'))
          setIsUploadingBadgeImage(false)
          return
        }
        setIsUploadingBadgeImage(false)
      }

      const badgeData: CreateCollectionBadgeDto = {
        name: badgeName.trim(),
        description: badgeDescription.trim() || null,
        longDescriptionHtml: badgeLongDescription.trim() || null,
        imageId: finalImageId,
      }

      await setBadge.mutateAsync({
        collectionId: id,
        data: badgeData,
      })
      setBadgeDialogOpen(false)
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to save badge'
      setBadgeError(message)
    }
  }

  const handleDeleteBadgeConfirm = async () => {
    if (!id) return

    try {
      await deleteBadge.mutateAsync(id)
      setDeleteBadgeDialogOpen(false)
    } catch (error: any) {
      console.error('Failed to delete badge:', error)
    }
  }

  const isSaving = createCollection.isPending || updateCollection.isPending
  const isSavingBadge = setBadge.isPending || isUploadingBadgeImage
  const isSavingAchievement = createAchievement.isPending || updateAchievement.isPending || isUploadingIcon

  // Handle icon file selection
  const handleIconSelect = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setAchievementIconUrl(reader.result as string)
      setUploadedIconFile(file)
    }
    reader.readAsDataURL(file)
  }

  const handleIconClear = () => {
    setAchievementIconUrl(null)
    setAchievementIconId(null)
    setUploadedIconFile(null)
  }

  // Achievement handlers
  const openAddAchievementDialog = () => {
    setEditingAchievement(null)
    setAchievementName('')
    setAchievementShortDesc('')
    setAchievementFullDesc('')
    setTrackFailures(false)
    setAchievementError(null)
    setAchievementIconUrl(null)
    setAchievementIconId(null)
    setUploadedIconFile(null)
    setAchievementDialogOpen(true)
  }

  const openEditAchievementDialog = async (achievement: CollectionAchievementSummaryDto) => {
    if (!id) return
    try {
      const fullAchievement = await adventureCollectionsApi.getAchievement(id, achievement.id)
      setEditingAchievement(fullAchievement)
      setAchievementName(fullAchievement.name)
      setAchievementShortDesc(fullAchievement.shortDescription)
      setAchievementFullDesc(fullAchievement.fullDescription || '')
      setTrackFailures(fullAchievement.trackFailures)
      setAchievementError(null)
      setAchievementIconUrl(fullAchievement.iconUrl || null)
      setAchievementIconId(null) // We don't have the ID, only the URL
      setUploadedIconFile(null)
      setAchievementDialogOpen(true)
    } catch (error: any) {
      console.error('Failed to load achievement:', error)
    }
  }

  const handleSaveAchievement = async () => {
    if (!id) return
    setAchievementError(null)

    if (!achievementName.trim()) {
      setAchievementError('Achievement name is required')
      return
    }

    if (!achievementShortDesc.trim()) {
      setAchievementError('Short description is required')
      return
    }

    try {
      // Upload icon if a new file was selected
      let finalIconId = achievementIconId
      if (uploadedIconFile) {
        setIsUploadingIcon(true)
        try {
          const uploadResult = await adventureCollectionsApi.uploadAchievementIcon(id, uploadedIconFile)
          finalIconId = uploadResult.id
        } catch (uploadError: any) {
          setAchievementError('Failed to upload icon: ' + (uploadError.response?.data?.message || uploadError.message || 'Unknown error'))
          setIsUploadingIcon(false)
          return
        }
        setIsUploadingIcon(false)
      }

      if (editingAchievement) {
        await updateAchievement.mutateAsync({
          collectionId: id,
          achievementId: editingAchievement.id,
          data: {
            name: achievementName.trim(),
            shortDescription: achievementShortDesc.trim(),
            fullDescription: achievementFullDesc.trim() || null,
            trackFailures,
            iconId: finalIconId,
          },
        })
      } else {
        await createAchievement.mutateAsync({
          collectionId: id,
          data: {
            name: achievementName.trim(),
            shortDescription: achievementShortDesc.trim(),
            fullDescription: achievementFullDesc.trim() || null,
            trackFailures,
            iconId: finalIconId,
          },
        })
      }
      setAchievementDialogOpen(false)
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to save achievement'
      setAchievementError(message)
    }
  }

  const handleDeleteAchievementClick = (achievement: CollectionAchievementSummaryDto) => {
    setAchievementToDelete(achievement)
    setDeleteAchievementDialogOpen(true)
  }

  const handleDeleteAchievementConfirm = async () => {
    if (!id || !achievementToDelete) return

    try {
      await deleteAchievement.mutateAsync({
        collectionId: id,
        achievementId: achievementToDelete.id,
      })
      setDeleteAchievementDialogOpen(false)
      setAchievementToDelete(null)
    } catch (error: any) {
      console.error('Failed to delete achievement:', error)
    }
  }

  const openAchievementAdventuresDialog = (achievementId: string) => {
    setSelectedAchievementId(achievementId)
    setAdventureSearch('')
    setAchievementAdventuresDialogOpen(true)
  }

  // Filter and sort available adventures
  const filteredSortedAdventures = useMemo(() => {
    if (!availableAdventures) return []

    // Filter out adventures already in the achievement
    let filtered = availableAdventures.filter(
      (adventure: AvailableAdventureDto) =>
        !selectedAchievement?.adventures?.some((a) => a.adventureId === adventure.id)
    )

    // Apply search filter
    if (adventureSearch.trim()) {
      const searchLower = adventureSearch.toLowerCase()
      filtered = filtered.filter(
        (adventure: AvailableAdventureDto) =>
          adventure.title.toLowerCase().includes(searchLower) ||
          (adventure.description && adventure.description.toLowerCase().includes(searchLower))
      )
    }

    // Sort
    filtered.sort((a: AvailableAdventureDto, b: AvailableAdventureDto) => {
      let comparison = 0
      if (adventureSortField === 'title') {
        comparison = a.title.localeCompare(b.title)
      } else if (adventureSortField === 'createdDate') {
        comparison = new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      }
      return adventureSortDirection === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [availableAdventures, selectedAchievement?.adventures, adventureSearch, adventureSortField, adventureSortDirection])

  const handleSortChange = (field: 'title' | 'createdDate') => {
    if (adventureSortField === field) {
      setAdventureSortDirection(adventureSortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setAdventureSortField(field)
      setAdventureSortDirection(field === 'createdDate' ? 'desc' : 'asc')
    }
  }

  const handleAddAdventureToAchievement = async (adventureId: string) => {
    if (!id || !selectedAchievementId) return

    try {
      await addAchievementAdventure.mutateAsync({
        collectionId: id,
        achievementId: selectedAchievementId,
        data: { adventureId },
      })
    } catch (error: any) {
      console.error('Failed to add adventure to achievement:', error)
    }
  }

  const handleRemoveAdventureFromAchievement = async (adventureId: string) => {
    if (!id || !selectedAchievementId) return

    try {
      await removeAchievementAdventure.mutateAsync({
        collectionId: id,
        achievementId: selectedAchievementId,
        adventureId,
      })
    } catch (error: any) {
      console.error('Failed to remove adventure from achievement:', error)
    }
  }

  // Maintainer handlers
  const handleAddMaintainer = async (item: MaintainerSearchResult) => {
    if (!id) return
    setMaintainerError(null)

    try {
      await addMaintainer.mutateAsync({
        collectionId: id,
        data: item.type === 'user' ? { userId: item.userId } : { groupId: item.groupId },
      })
      setMaintainerSearchQuery('')
      setMaintainerSearchResults([])
    } catch (error: any) {
      console.error('Failed to add maintainer:', error)
      const message = error.response?.data?.message || error.response?.data || 'Failed to add maintainer'
      setMaintainerError(message)
    }
  }

  const handleRemoveMaintainerClick = (maintainer: CollectionMaintainerDto) => {
    setMaintainerToRemove(maintainer)
    setRemoveMaintainerDialogOpen(true)
  }

  const handleRemoveMaintainerConfirm = async () => {
    if (!id || !maintainerToRemove) return

    try {
      await removeMaintainer.mutateAsync({
        collectionId: id,
        maintainerId: maintainerToRemove.id,
      })
      setRemoveMaintainerDialogOpen(false)
      setMaintainerToRemove(null)
      setMaintainerError(null)
    } catch (error: any) {
      console.error('Failed to remove maintainer:', error)
      const message = error.response?.data?.message || error.response?.data || 'Failed to remove maintainer'
      setMaintainerError(message)
    }
  }

  // Redirect to my-adventures if user can't edit this collection
  const canEdit = collection?.canEdit
  useEffect(() => {
    if (isEditMode && collection && !canEdit) {
      navigate('/my-adventures', { replace: true })
    }
  }, [isEditMode, collection, canEdit, navigate])

  if (isEditMode && isLoadingCollection) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isEditMode && !collection) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Collection not found
      </Alert>
    )
  }

  // Don't render editor if user can't edit (will redirect)
  if (isEditMode && collection && !collection.canEdit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Get achievements from collection.achievements (now inline in response)
  const achievements = collection?.achievements || []

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {isEditMode ? 'Edit Collection' : 'Create Collection'}
        </Typography>
      </Box>

      {formError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {formError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Collection Details Form */}
        <Grid item xs={12} md={isEditMode ? 6 : 12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Collection Details
            </Typography>

            <TextField
              fullWidth
              label="Collection Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 3 }}
              placeholder="e.g., Historic Downtown Tour"
            />

            <TextField
              fullWidth
              label="Short Description"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 3 }}
              placeholder="A brief summary of the collection"
              helperText="Shown in collection cards and previews"
            />

            <TextField
              fullWidth
              label="Full Description"
              value={fullDescription}
              onChange={(e) => setFullDescription(e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 3 }}
              placeholder="Detailed description of what this collection offers"
              helperText="Shown on the collection detail page"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              }
              label="Make this collection public"
              sx={{ mb: 3 }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -2, mb: 3, ml: 6 }}>
              Public collections can be discovered and saved by other users
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Collection'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Achievements and Badge sections (Edit mode only) */}
        {isEditMode && (
          <Grid item xs={12} md={6}>
            {/* Achievements Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Achievements ({achievements.length})
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={openAddAchievementDialog}
                  size="small"
                >
                  Add Achievement
                </Button>
              </Box>

              {achievements.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {achievements.map((achievement: CollectionAchievementSummaryDto) => (
                    <Card key={achievement.id} variant="outlined">
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                        <Avatar
                          variant="rounded"
                          src={achievement.iconUrl || undefined}
                          sx={{
                            width: 48,
                            height: 48,
                            mr: 2,
                            backgroundColor: 'secondary.light',
                            borderRadius: 2,
                          }}
                        >
                          <MilitaryTech />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {achievement.name}
                            </Typography>
                            <Chip
                              label={`${achievement.totalAdventureCount} adventures`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                            {!achievement.isActive && (
                              <Chip
                                label="Inactive"
                                size="small"
                                color="default"
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {achievement.shortDescription}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => openAchievementAdventuresDialog(achievement.id)}
                          title="Manage Adventures"
                          sx={{ mr: 0.5 }}
                        >
                          <Settings fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openEditAchievementDialog(achievement)}
                          sx={{ mr: 0.5 }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAchievementClick(achievement)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <MilitaryTech sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No achievements configured yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Create achievements to organize adventures that users can complete to earn rewards
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openAddAchievementDialog}
                  >
                    Add First Achievement
                  </Button>
                </Paper>
              )}
            </Paper>

            {/* Badge Section - Single badge per collection */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Collection Badge
                </Typography>
                {!collection?.badge && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={openBadgeDialog}
                    size="small"
                  >
                    Add Badge
                  </Button>
                )}
              </Box>

              {collection?.badge ? (
                <Card variant="outlined">
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
                    <Avatar
                      variant="rounded"
                      src={collection.badge.imageUrl || undefined}
                      sx={{
                        width: 48,
                        height: 48,
                        mr: 2,
                        backgroundColor: 'warning.light',
                        borderRadius: 2,
                      }}
                    >
                      <EmojiEvents />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {collection.badge.name}
                        </Typography>
                      </Box>
                      {collection.badge.description && (
                        <Typography variant="caption" color="text.secondary">
                          {collection.badge.description}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={openBadgeDialog}
                      sx={{ mr: 0.5 }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteBadgeDialogOpen(true)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No collection badge configured
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Add a badge that users earn by completing this collection
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openBadgeDialog}
                  >
                    Add Collection Badge
                  </Button>
                </Paper>
              )}
            </Paper>

            {/* Maintainers Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Maintainers
              </Typography>

              {maintainerError && (
                <Alert severity="error" onClose={() => setMaintainerError(null)} sx={{ mb: 2 }}>
                  {maintainerError}
                </Alert>
              )}

              {/* Add maintainer search */}
              <Box sx={{ mb: 2 }}>
                <Autocomplete<MaintainerSearchResult, false, false, true>
                  freeSolo
                  options={maintainerSearchResults}
                  filterOptions={(x) => x}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option
                    return option.displayName || ''
                  }}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props
                    const isGroup = option.type === 'group'

                    return (
                      <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={option.profileImageUrl}
                          alt={option.displayName}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: isGroup ? 'primary.main' : undefined,
                          }}
                        >
                          {isGroup ? <Group fontSize="small" /> : <Person fontSize="small" />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.displayName}
                            </Typography>
                            {isGroup && (
                              <Chip
                                label="Group"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          {isGroup && option.memberCount !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {option.memberCount} member{option.memberCount !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )
                  }}
                  inputValue={maintainerSearchQuery}
                  onInputChange={(_, value) => setMaintainerSearchQuery(value)}
                  onChange={(_, value) => {
                    if (value && typeof value === 'object') {
                      handleAddMaintainer(value)
                    }
                  }}
                  loading={isMaintainerSearching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search users or groups to add..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {isMaintainerSearching ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Current maintainers list */}
              {isLoadingMaintainers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : maintainers && maintainers.length > 0 ? (
                <List dense disablePadding>
                  {maintainers.map((maintainer) => (
                    <ListItem
                      key={maintainer.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMaintainerClick(maintainer)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                      sx={{ px: 0 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: maintainer.isGroup ? 'primary.main' : undefined,
                          }}
                        >
                          {maintainer.isGroup ? <Group fontSize="small" /> : <Person fontSize="small" />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {maintainer.isGroup ? maintainer.group?.name : maintainer.user?.displayName}
                            </Typography>
                            {maintainer.isGroup && (
                              <Chip
                                label="Group"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          maintainer.isGroup && maintainer.group?.description
                            ? maintainer.group.description.substring(0, 50) + (maintainer.group.description.length > 50 ? '...' : '')
                            : undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No maintainers assigned yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Add users or groups who can edit this collection
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Badge Dialog */}
      <Dialog
        open={badgeDialogOpen}
        onClose={() => setBadgeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{collection?.badge ? 'Edit Badge' : 'Add Badge'}</DialogTitle>
        <DialogContent>
          {badgeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {badgeError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Badge Name"
            value={badgeName}
            onChange={(e) => setBadgeName(e.target.value)}
            required
            sx={{ mt: 1, mb: 3 }}
            placeholder="e.g., Collection Master"
          />

          <TextField
            fullWidth
            label="Short Description"
            value={badgeDescription}
            onChange={(e) => setBadgeDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 3 }}
            placeholder="Brief description of the badge"
          />

          <TextField
            fullWidth
            label="Detailed Description (HTML)"
            value={badgeLongDescription}
            onChange={(e) => setBadgeLongDescription(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3 }}
            placeholder="Optional: Longer description with HTML formatting"
            helperText="Shown when users view badge details"
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Badge Image
          </Typography>
          <Box>
            <ImageUploadZone
              value={badgeImageUrl}
              onFileSelect={handleBadgeImageSelect}
              onClear={handleBadgeImageClear}
              maxSizeMB={2}
              label="Upload Badge Image"
              description="Drag and drop, paste, or click to select"
              isUploading={isUploadingBadgeImage}
              disabled={isSavingBadge}
              aspectRatio="1:1"
              allowCamera={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBadgeDialogOpen(false)} disabled={isSavingBadge}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBadge}
            disabled={isSavingBadge}
          >
            {isSavingBadge ? 'Saving...' : collection?.badge ? 'Update Badge' : 'Create Badge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Badge Confirmation Dialog */}
      <Dialog open={deleteBadgeDialogOpen} onClose={() => setDeleteBadgeDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Badge</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the badge "{collection?.badge?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBadgeDialogOpen(false)} disabled={deleteBadge.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBadgeConfirm}
            color="error"
            variant="contained"
            disabled={deleteBadge.isPending}
          >
            {deleteBadge.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Achievement Dialog */}
      <Dialog
        open={achievementDialogOpen}
        onClose={() => setAchievementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingAchievement ? 'Edit Achievement' : 'Add Achievement'}</DialogTitle>
        <DialogContent>
          {achievementError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {achievementError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Achievement Name"
            value={achievementName}
            onChange={(e) => setAchievementName(e.target.value)}
            required
            sx={{ mt: 1, mb: 3 }}
            placeholder="e.g., Trail Master"
          />

          <TextField
            fullWidth
            label="Short Description"
            value={achievementShortDesc}
            onChange={(e) => setAchievementShortDesc(e.target.value)}
            required
            multiline
            rows={2}
            sx={{ mb: 3 }}
            placeholder="Brief description of the achievement"
          />

          <TextField
            fullWidth
            label="Full Description (HTML)"
            value={achievementFullDesc}
            onChange={(e) => setAchievementFullDesc(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 3 }}
            placeholder="Optional: Detailed description with HTML formatting"
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Achievement Icon
          </Typography>
          <Box sx={{ mb: 3 }}>
            <ImageUploadZone
              value={achievementIconUrl}
              onFileSelect={handleIconSelect}
              onClear={handleIconClear}
              maxSizeMB={2}
              label="Upload Icon"
              description="Drag and drop, paste, or click to select"
              isUploading={isUploadingIcon}
              disabled={isSavingAchievement}
              aspectRatio="1:1"
              allowCamera={false}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={trackFailures}
                onChange={(e) => setTrackFailures(e.target.checked)}
              />
            }
            label="Track Failures"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mb: 2 }}>
            When enabled, failed attempts on adventures in this achievement will be tracked
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAchievementDialogOpen(false)} disabled={isSavingAchievement}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAchievement}
            disabled={isSavingAchievement}
          >
            {isSavingAchievement ? 'Saving...' : editingAchievement ? 'Update Achievement' : 'Create Achievement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Achievement Adventures Dialog */}
      <Dialog
        open={achievementAdventuresDialogOpen}
        onClose={() => setAchievementAdventuresDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Manage Achievement Adventures</DialogTitle>
        <DialogContent dividers>
          {isLoadingSelectedAchievement ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Current adventures in achievement */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Adventures in Achievement ({selectedAchievement?.adventures?.length || 0})
              </Typography>

              {selectedAchievement?.adventures && selectedAchievement.adventures.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAchievement.adventures.map((adv) => (
                        <TableRow key={adv.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{adv.adventureTitle}</TableCell>
                          <TableCell>
                            {adv.adventureDescription && (
                              <Box
                                component="span"
                                sx={{
                                  display: 'block',
                                  maxHeight: 60,
                                  overflow: 'hidden',
                                  fontSize: '0.875rem',
                                  color: 'text.secondary',
                                  '& p': { m: 0 },
                                  '& *': { fontSize: 'inherit' },
                                }}
                                dangerouslySetInnerHTML={{ __html: adv.adventureDescription }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveAdventureFromAchievement(adv.adventureId)}
                              disabled={removeAchievementAdventure.isPending}
                              title="Remove from achievement"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    textAlign: 'center',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No adventures in this achievement yet
                  </Typography>
                </Paper>
              )}

              {/* Available adventures to add */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Available Adventures ({filteredSortedAdventures.length})
                </Typography>
              </Box>

              {/* Search field */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search adventures by title or description..."
                value={adventureSearch}
                onChange={(e) => setAdventureSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {isLoadingAvailable ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : availableAdventures && availableAdventures.length > 0 ? (
                filteredSortedAdventures.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            <TableSortLabel
                              active={adventureSortField === 'title'}
                              direction={adventureSortField === 'title' ? adventureSortDirection : 'asc'}
                              onClick={() => handleSortChange('title')}
                            >
                              Title
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: 120 }}>
                            <TableSortLabel
                              active={adventureSortField === 'createdDate'}
                              direction={adventureSortField === 'createdDate' ? adventureSortDirection : 'desc'}
                              onClick={() => handleSortChange('createdDate')}
                            >
                              Created
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSortedAdventures.map((adventure: AvailableAdventureDto) => (
                          <TableRow key={adventure.id} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{adventure.title}</TableCell>
                            <TableCell>
                              {adventure.description && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'block',
                                    maxHeight: 60,
                                    overflow: 'hidden',
                                    fontSize: '0.875rem',
                                    color: 'text.secondary',
                                    '& p': { m: 0 },
                                    '& *': { fontSize: 'inherit' },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: adventure.description }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(adventure.createdDate).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAddAdventureToAchievement(adventure.id)}
                                disabled={addAchievementAdventure.isPending}
                              >
                                Add
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : adventureSearch.trim() ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No adventures match your search "{adventureSearch}"
                    </Typography>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All available adventures are already in this achievement.
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No adventures available. Create adventures first to add them to achievements.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAchievementAdventuresDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Achievement Confirmation Dialog */}
      <Dialog open={deleteAchievementDialogOpen} onClose={() => setDeleteAchievementDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Achievement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the achievement "{achievementToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAchievementDialogOpen(false)} disabled={deleteAchievement.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAchievementConfirm}
            color="error"
            variant="contained"
            disabled={deleteAchievement.isPending}
          >
            {deleteAchievement.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Maintainer Confirmation Dialog */}
      <Dialog open={removeMaintainerDialogOpen} onClose={() => setRemoveMaintainerDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Remove Maintainer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{maintainerToRemove?.isGroup ? maintainerToRemove?.group?.name : maintainerToRemove?.user?.displayName}" as a maintainer?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveMaintainerDialogOpen(false)} disabled={removeMaintainer.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveMaintainerConfirm}
            color="error"
            variant="contained"
            disabled={removeMaintainer.isPending}
          >
            {removeMaintainer.isPending ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
